import React, {
  useEffect,
  useState,
  createContext,
  useContext,
  useCallback,
} from 'react';

import { OnboardingController as OnboardingControllerAPI } from 'api';
import type { IOnboardingSubmitProfileBody } from '@/app-package';
import { OrgOnboardingType } from '@bb/users';
import { Modal } from 'components';

import { useBreakpoint } from 'hooks';
import { buildWizardUrl, buildSSORedirect } from '../util';
import { ClaimPrefix, OrgOnboardingStep } from '../constants';

import { NotFoundView } from '../pages/common/NotFoundView';
import { LoadingView } from '../pages/common/LoadingView';

import {
  UserLocalOnboardingProfile,
  UserRemoteOnboardingProfile,
  WizardType,
} from '../types';

export const HASH_KEY = 'bb.onboarding~user~hash';
export const TOS_REDIRECT_CONTENT = {
  title: 'Looks like something went wrong',
  content: (
    <>
      <br />
      <>There was an issue accessing your profile information.</>
      <br />
      <br />
      <>
        You will be redirected to the start of the claim brokerage experience.
        Please complete all necessary steps before closing this browser session.
      </>
    </>
  ),
};

type UserOnboardingContextValue = {
  isFetchingProfile: boolean;
  userClaimHash: string;
  userProfile: UserLocalOnboardingProfile;
  newPassword: boolean | string;

  /** update client side cache of user profile */
  updateLocalUserProfile: (
    nextProfile: UserLocalOnboardingProfile,
    newHash?: string,
  ) => void;

  completeOnboardingUrl: () => Promise<string>;

  /** update server side cache of user profile */
  saveUserProfile: (ssoRedirect?: string) => Promise<void>;

  setNewPassword: React.Dispatch<React.SetStateAction<string>>;
  saveLocalHash: (hashToSave: string) => void;
  claimBrokerage: (
    orgClaimHash: string,
    opts: { verifyHash?: string; isClaimed?: boolean },
  ) => Promise<void>;
  isUnclaimedApprover?: boolean;
  setIsUnclaimedApprover: (val: boolean) => void;
};

export const UserOnboardingContext = createContext<
  undefined | UserOnboardingContextValue
>(undefined);

// #region Static Functions

export const useUserOnboardingContext = () => {
  const onboardingContext = useContext(UserOnboardingContext);
  //
  // wait for the local profile to be populated from localstorage before proceeding.
  //
  if (!onboardingContext) {
    return null;
  }

  return onboardingContext;
};

const createLocalStorageKey = (hash: string) => `bb.onboarding~${hash}`;

const removeUserProfileFromLocalStorage = (hash: string) => {
  try {
    const key = createLocalStorageKey(hash);
    localStorage.removeItem(key);
  } catch (e) {
    // fixme: do something better with errors here
    // eslint-disable-next no-console
    console.warn(e.getMessage());
  }
};

const readUserProfileFromLocalStorage = (
  profile: UserRemoteOnboardingProfile,
  hash: string,
) => {
  try {
    const key = createLocalStorageKey(hash);
    const value = localStorage.getItem(key);

    return { phone: '', ...profile, ...JSON.parse(value) };
  } catch (e) {
    // fixme: do something better with errors here
    // eslint-disable-next no-console
    console.warn(e.getMessage());

    return profile;
  }
};

export const saveUserProfileToLocalStorage = (
  profile: UserRemoteOnboardingProfile,
  hash: string,
) => {
  const key = createLocalStorageKey(hash);
  const value = JSON.stringify(profile);

  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // fixme: do something better with errors here
    // eslint-disable-next no-console
    console.error(e.getMessage());
  }
};

export const removeHashFromStorage = () => {
  localStorage.removeItem(HASH_KEY);
};

export const writeHashToStorage = (hash: string) => {
  localStorage.setItem(HASH_KEY, hash);
};

export const readHashFromStorage = () => localStorage.getItem(HASH_KEY);

const setBBUrl = () => {
  sessionStorage.setItem('bburl', '#/my_business?showWelcome=true');
};

const setLoginRedirectUrl = (url: string) => {
  sessionStorage.setItem('bbloginredirect', url);
};

const generateNoProfileRedirect = (orgClaimHash: string) => {
  const url = buildWizardUrl({
    prefix: ClaimPrefix.ORG_PROFILE,
    urlParams: [{ name: 'hash', value: orgClaimHash }],
    nextStep: OrgOnboardingStep.PRIMARY_ROLE,
  });
  const result = `/onboarding/#${url}`;
  return result;
};

// #endregion Static Functions

type UserOnboardingContextProviderProps = { hash?: string; type?: WizardType };

export const UserOnboardingContextProvider: React.FC<
  UserOnboardingContextProviderProps
> = props => {
  const { hash, type = 'user', children } = props;

  const [remoteProfile, setRemoteProfile] =
    useState<UserRemoteOnboardingProfile | null>(null);
  const [localHash, setLocalHash] =
    useState<UserOnboardingContextProviderProps['hash']>(hash);

  const [localProfile, setLocalProfile] =
    useState<UserLocalOnboardingProfile | null>(null);
  const [isFetchingProfile, setIsFetchingProfile] = useState(true);
  const [newPassword, setNewPassword] = useState<string>();
  const [userFound, setUserFound] = useState(true);
  const { md } = useBreakpoint();

  // This flag is set based on `approver` query param when the user lands on
  // GetStartedView and determines where to send the user at the end of the
  // claim flow (unclaimed approvers that get sent here on approving their
  // brokerage setup should see the 'Approval Submitted!' success message
  // instead of being directed to the app).
  const [isUnclaimedApprover, setIsUnclaimedApprover] = useState<boolean>();

  const saveLocalHash = (hashToSave: string) => {
    writeHashToStorage(hashToSave);
    setLocalHash(hashToSave);
  };

  const updateLocalUserProfile = (
    nextProfile: UserLocalOnboardingProfile,
    newHash?: string,
  ) => {
    const combinedNextProfile = {
      ...remoteProfile,
      ...localProfile,
      ...nextProfile,
    };

    const storageHash = newHash || localHash;

    if (newHash) {
      saveLocalHash(newHash);
    }

    saveUserProfileToLocalStorage(combinedNextProfile, storageHash);
    setLocalProfile(combinedNextProfile);
  };

  const completeOnboardingUrl = async () => {
    let redirectUrl: string;
    if (sessionStorage.getItem('bbloginredirect')) {
      redirectUrl = sessionStorage.getItem('bbloginredirect')!;
    } else {
      redirectUrl = '/redirect_login';
    }
    return `${
      window.location.origin
    }/app/login?redirectUrl=${encodeURIComponent(redirectUrl)}`;
  };

  const saveUserProfile = async (ssoRedirect?: string) => {
    const {
      firstName,
      lastName,
      email,
      phone,
      avatarFilestackId,
      avatarFullFilestackId,
      mls_numbers,
    } = localProfile;

    const updateBody: IOnboardingSubmitProfileBody = {
      firstName,
      lastName,
      email,
      phone,
      mls_numbers,
      password: newPassword,
      ...(avatarFilestackId && { avatarFilestackId }),
      ...(avatarFullFilestackId && { avatarFullFilestackId }),
    };

    const { redirect } = await OnboardingControllerAPI.submitProfile(
      { claimHash: localHash },
      updateBody,
    );

    if (ssoRedirect) {
      const targetRedirect = buildSSORedirect(ssoRedirect);

      setLoginRedirectUrl(targetRedirect);
      return;
    }

    if (isUnclaimedApprover) {
      setLoginRedirectUrl('/onboarding/approval-submitted');
      return;
    }

    _cleanUpContext();
    setBBUrl();
    setLoginRedirectUrl(redirect);
  };

  const claimBrokerage: UserOnboardingContextValue['claimBrokerage'] = async (
    orgClaimHash,
    { verifyHash, isClaimed },
  ) => {
    const params: Parameters<
      (typeof OnboardingControllerAPI)['claimBrokerage']
    > = [
      { submitterHash: verifyHash, orgClaimHash },
      { onboardingType: OrgOnboardingType.UNLICENSED },
    ];

    // localProfile may be null here due to submitter claiming their profile through
    // user onboarding during the org onboarding process
    if (!isClaimed) {
      if (localProfile) {
        const {
          firstName,
          lastName,
          email,
          phone,
          avatarFilestackId,
          avatarFullFilestackId,
          id,
        } = localProfile;

        params[1].unclaimedSubmitter = {
          firstName,
          lastName,
          email,
          phone,
          password: newPassword,
          ...(avatarFilestackId && { avatarFilestackId }),
          ...(avatarFullFilestackId && { avatarFullFilestackId }),
        };

        if (id) {
          params[1].onboardingType = OrgOnboardingType.LICENSED;
        }
      } else {
        Modal.warning({
          ...TOS_REDIRECT_CONTENT,
          onOk: () =>
            window.location.assign(generateNoProfileRedirect(orgClaimHash)),
        });
        return;
      }
    }
    const { redirect } = await OnboardingControllerAPI.claimBrokerage(
      ...params,
    );

    _cleanUpContext();
    setBBUrl();
    return window.location.assign(redirect);
  };

  const _cleanUpContext = () => {
    removeUserProfileFromLocalStorage(localHash);
    removeHashFromStorage();
  };

  useEffect(() => {
    setLocalHash(hash);
  }, [hash]);

  useEffect(() => {
    if (!localHash) return;

    const key = createLocalStorageKey(localHash);
    const localStorageProfile = JSON.parse(localStorage.getItem(key));

    const fetchProfile = async () => {
      try {
        setIsFetchingProfile(true);
        const profile = await OnboardingControllerAPI.getProfile({
          hash: localHash,
        });

        setRemoteProfile(profile);

        setLocalProfile(currentProfile => {
          if (currentProfile?.id !== profile.id) {
            const tempProfile = readUserProfileFromLocalStorage(
              profile,
              localHash,
            );
            return tempProfile;
          }
          return currentProfile;
        });
      } catch (err) {
        setUserFound(false);
        setLocalProfile(localStorageProfile);
      } finally {
        setIsFetchingProfile(false);
      }
    };

    fetchProfile();
  }, [localHash, type]);

  // hash is not a required prop in the unlicensed submitter flow for org onboarding
  // must allow render even if it is not passed in
  if (type !== 'org' && isFetchingProfile) {
    return <LoadingView includeLayout />;
  }

  // No user could be found due to stale/invalid claimhash
  if (!isFetchingProfile && !userFound && type !== 'org') {
    return <NotFoundView />;
  }

  return (
    <UserOnboardingContext.Provider
      value={{
        isFetchingProfile,
        userClaimHash: localHash,
        userProfile: localProfile,
        completeOnboardingUrl,
        saveUserProfile,
        updateLocalUserProfile,
        newPassword,
        setNewPassword,
        saveLocalHash,
        isUnclaimedApprover,
        setIsUnclaimedApprover,
        claimBrokerage,
      }}
    >
      {children}
    </UserOnboardingContext.Provider>
  );
};
